/**
 * The controls.
 *
 * Every choice these components offer comes from the catalog: which properties
 * exist, which operators each accepts, how many inputs an operator needs, which
 * input to render, which values are legal, which events can follow a subject,
 * which units time is expressed in, who the actor may be. None of it is
 * enumerated here — a `switch` over operator names, or a list of measure
 * functions, would be exactly the second analytics model this build is meant
 * not to have.
 *
 * Two things below are genuinely presentational, and are marked where they
 * appear: how an `arity` maps to a number of boxes, and how a `control` maps to
 * an HTML input. Both are questions about widgets, not about football.
 */

import { useEffect, useRef } from 'react'

import type { CatalogEvent, Named, Operator, Property, SubjectCatalog, TimeUnit } from './catalog'
import { eventBy, operatorBy, propertyBy } from './catalog'
import { halfOf, overrun, tilingWidths } from './geometry'
import type { PitchDims } from './run'
import type { Breakdown, Comparison, KnownStep, Measure, SequenceStep } from './spec'
import { isPropertyOperand, isSequence } from './spec'

const asText = (v: unknown): string => (v === null || v === undefined ? '' : String(v))

/** Show a value the way the analyst chose it, not the way JSON stores it. */
/** Metres, without the noise: 6.5 stays 6.5, 11.0 prints as 11. */
const round = (v: number): string => String(Math.round(v * 100) / 100)

const label = (v: unknown): string => (typeof v === 'boolean' ? (v ? 'Yes' : 'No') : asText(v))

// ------------------------------------------------------------------- values

interface ValueInputProps {
  prop: Property
  op: Operator | undefined
  value: unknown
  /** Measured values for this property, when the catalog says it has them. */
  values: unknown[] | undefined
  loading: boolean
  onChange: (value: unknown) => void
}

/**
 * The right-hand side of a comparison.
 *
 * `arity` decides how many boxes ("is between" needs two, "is known" needs
 * none); `control` and `type` decide what kind of box. Both arrive from the
 * catalog, which is why neither operator names nor property names appear here.
 *
 * Options carry their INDEX, not their text: receiver 16 is the number 16 and a
 * `<select>` would hand back the string "16", which the compiler would compare
 * against an integer column and quietly match nothing.
 */
/**
 * The arity-many list box, with the selection SAID rather than implied.
 *
 * A native multi-select is a poor witness to its own state: unfocused
 * selections render as faint gray on macOS, the box never scrolls to a
 * selected row (a loaded spec's matches can all sit below the visible
 * window), and a chosen value the measured list no longer carries simply
 * un-highlights with no message. Loading a saved spec looked like "the
 * matches didn't select" for all three reasons at once (Ben, 2026-09-05).
 * So: the selection is written out beneath the box, the box scrolls to the
 * first selected row when the selection changes from outside, and a pinned
 * value missing from the list is called out instead of dropped from view.
 */
function MultiPicker({ prop, values, chosen, indexOf, onChange }: {
  prop: Property
  values: unknown[]
  chosen: unknown[]
  indexOf: (v: unknown) => string
  onChange: (value: unknown) => void
}) {
  const ref = useRef<HTMLSelectElement>(null)
  const missing = chosen.filter((v) => indexOf(v) === '')
  // Scroll the first selected option into view when the selection arrives
  // from outside the widget (loading a spec) — a user's own click is
  // already in view. Keyed on content, not identity, so typing elsewhere
  // does not yank the scroll position.
  const chosenKey = chosen.map(indexOf).join(',')
  useEffect(() => {
    const first = ref.current?.selectedOptions?.[0]
    // scrollIntoView is missing from jsdom; the guard is for tests, not users.
    if (first && typeof first.scrollIntoView === 'function') {
      first.scrollIntoView({ block: 'nearest' })
    }
  }, [chosenKey])
  return (
    <span className="vb-multi-wrap">
      <select
        ref={ref}
        className="adm-select vb-multi"
        multiple
        size={Math.min(8, values.length)}
        aria-label={`${prop.label} values`}
        value={chosen.map(indexOf)}
        onChange={(e) => onChange(
          Array.from(e.target.selectedOptions).map((o) => values[Number(o.value)]),
        )}
      >
        {values.map((v, i) => <option key={i} value={i}>{label(v)}</option>)}
      </select>
      <span className="adm-note vb-multi-note" aria-label={`${prop.label} selection`}>
        {chosen.length
          ? `Selected (${chosen.length} of ${values.length}): ${chosen.map(label).join(', ')}`
          : 'Nothing selected'}
      </span>
      {missing.length ? (
        <span className="adm-msg adm-msg--err vb-picker-missing">
          {`Not in the measured list: ${missing.map(label).join(', ')} — still in the spec, but no data matches`}
        </span>
      ) : null}
    </span>
  )
}


export function ValueInput({ prop, op, value, values, loading, onChange }: ValueInputProps) {
  if (!op || op.arity === 'none') return null

  const pick = (raw: string): unknown => (raw === '' ? undefined : values?.[Number(raw)])
  const indexOf = (v: unknown): string => {
    const i = (values ?? []).findIndex((c) => c === v)
    return i < 0 ? '' : String(i)
  }

  if (prop.has_values) {
    if (loading) return <span className="adm-note">loading values…</span>
    if (!values || values.length === 0) return <span className="adm-note">no values found</span>

    if (op.arity === 'many') {
      const chosen = Array.isArray(value) ? (value as unknown[]) : []
      return (
        <MultiPicker
          prop={prop} values={values} chosen={chosen}
          indexOf={indexOf} onChange={onChange}
        />
      )
    }
    // A pinned value the measured list does not carry (a stale saved spec,
    // a renamed match) must be SAID, not silently rendered as "choose…" —
    // the authored spec still holds it and would still be sent.
    const single = value !== undefined && value !== null && indexOf(value) === ''
    return (
      <>
        <select
          className="adm-select"
          aria-label={`${prop.label} value`}
          value={indexOf(value)}
          onChange={(e) => onChange(pick(e.target.value))}
        >
          <option value="">choose…</option>
          {values.map((v, i) => <option key={i} value={i}>{label(v)}</option>)}
        </select>
        {single ? (
          <span className="adm-msg adm-msg--err vb-picker-missing">
            {`Spec pins ${label(value)}, which is not in the measured list`}
          </span>
        ) : null}
      </>
    )
  }

  // No measured list: a free input, typed by the property.
  const num = (raw: string): unknown => (raw === '' ? undefined : Number(raw))

  if (op.arity === 'two') {
    const pair = Array.isArray(value) ? (value as unknown[]) : []
    const at = (i: number, v: unknown): unknown[] => {
      const next = [pair[0], pair[1]]
      next[i] = v
      return next
    }
    return (
      <>
        <input
          className="adm-input vb-num" type="number" aria-label={`${prop.label} from`}
          value={asText(pair[0])} onChange={(e) => onChange(at(0, num(e.target.value)))}
        />
        <span className="adm-note">and</span>
        <input
          className="adm-input vb-num" type="number" aria-label={`${prop.label} to`}
          value={asText(pair[1])} onChange={(e) => onChange(at(1, num(e.target.value)))}
        />
        {prop.unit_label ? <span className="adm-note">{prop.unit_label}</span> : null}
      </>
    )
  }

  if (prop.control === 'toggle' || prop.type === 'boolean') {
    return (
      <select
        className="adm-select" aria-label={`${prop.label} value`}
        value={value === true ? 'true' : value === false ? 'false' : ''}
        onChange={(e) => onChange(e.target.value === '' ? undefined : e.target.value === 'true')}
      >
        <option value="">choose…</option>
        <option value="true">Yes</option>
        <option value="false">No</option>
      </select>
    )
  }

  if (prop.type === 'number') {
    return (
      <>
        <input
          className="adm-input vb-num" type="number" aria-label={`${prop.label} value`}
          value={asText(value)} onChange={(e) => onChange(num(e.target.value))}
        />
        {prop.unit_label ? <span className="adm-note">{prop.unit_label}</span> : null}
      </>
    )
  }

  return (
    <input
      className="adm-input" type="text" aria-label={`${prop.label} value`}
      value={asText(value)} onChange={(e) => onChange(e.target.value)}
    />
  )
}

// --------------------------------------------------------------- conditions

interface ConditionRowProps {
  cat: SubjectCatalog
  condition: Comparison
  /** Which properties this row may switch to. Fixed for a required property. */
  choices: Property[]
  values: unknown[] | undefined
  loading: boolean
  onChange: (c: Comparison) => void
  onRemove?: () => void
}

/**
 * One comparison: property, operator, value.
 *
 * `unit` is attached whenever the catalog says the property has one, because
 * the schema requires it — "so a spec is never silently misread (metres vs
 * centimetres)". The builder therefore cannot express a unitless distance, and
 * that is the point.
 */
export function ConditionRow(
  { cat, condition, choices, values, loading, onChange, onRemove }: ConditionRowProps,
) {
  const prop = propertyBy(cat, condition.property)
  const op = prop ? operatorBy(prop, condition.op) : undefined

  const retarget = (name: string): void => {
    const next = propertyBy(cat, name)
    const first = next?.operators[0]
    onChange({ property: name, op: first ? first.name : condition.op })
  }
  const reoperate = (name: string): void => {
    const chosen = prop ? operatorBy(prop, name) : undefined
    const out: Comparison = { property: condition.property, op: name }
    // Carry the value across only when the new operator still takes one shaped
    // like it; anything else would leave a stale value in the spec.
    if (chosen && op && chosen.arity === op.arity) {
      out.value = condition.value
      if (condition.unit) out.unit = condition.unit
    }
    onChange(out)
  }
  const revalue = (value: unknown): void => {
    const out: Comparison = { property: condition.property, op: condition.op, value }
    if (prop?.unit) out.unit = prop.unit
    onChange(out)
  }

  return (
    <div className="adm-row vb-row">
      {choices.length > 1 ? (
        <select
          className="adm-select" aria-label="Property"
          value={condition.property} onChange={(e) => retarget(e.target.value)}
        >
          {choices.map((p) => <option key={p.name} value={p.name}>{p.label}</option>)}
        </select>
      ) : <span className="vb-fixed">{prop?.label ?? condition.property}</span>}

      <select
        className="adm-select" aria-label="Operator"
        value={condition.op} onChange={(e) => reoperate(e.target.value)}
      >
        {(prop?.operators ?? []).map((o) => (
          <option key={o.name} value={o.name}>{o.label}</option>
        ))}
      </select>

      {prop ? (
        <ValueInput
          prop={prop} op={op} value={condition.value} values={values}
          loading={loading} onChange={revalue}
        />
      ) : null}

      {onRemove ? (
        <button type="button" className="adm-btn adm-btn--quiet" onClick={onRemove}>
          Remove
        </button>
      ) : null}
    </div>
  )
}

// ---------------------------------------------------------------- sequences

interface SequenceRowProps {
  cat: SubjectCatalog
  step: SequenceStep
  /**
   * Which slot this sequence sits in. The predicate is identical either way —
   * only the slot decides whether it filters the population or measures it.
   */
  slot: 'filter' | 'measure'
  onChange: (s: SequenceStep) => void
  onMove: () => void
  onRemove: () => void
}

export function SequenceRow({ cat, step, slot, onChange, onMove, onRemove }: SequenceRowProps) {
  const units = cat.time_units
  const actors = cat.sequence_actors
  const subject = cat.subject.label.toLowerCase()

  return (
    <div className="adm-row vb-row">
      <span className="vb-fixed">then</span>
      <select
        className="adm-select" aria-label="Following event"
        value={step.event}
        onChange={(e) => onChange({ ...step, event: e.target.value })}
      >
        {cat.events.map((ev: CatalogEvent) => (
          <option key={ev.name} value={ev.name}>{ev.label}</option>
        ))}
      </select>

      <span className="adm-note">by</span>
      <select
        className="adm-select" aria-label="Actor"
        value={step.by ?? actors.find((a: Named) => a.default)?.name ?? 'same_team'}
        onChange={(e) => onChange({ ...step, by: e.target.value })}
      >
        {actors.map((a: Named) => <option key={a.name} value={a.name}>{a.label}</option>)}
      </select>

      <span className="adm-note">within</span>
      <input
        className="adm-input vb-num" type="number" min={0} aria-label="Window"
        value={asText(step.within.value)}
        onChange={(e) => onChange({
          ...step, within: { ...step.within, value: Number(e.target.value) },
        })}
      />
      <select
        className="adm-select" aria-label="Window unit"
        value={step.within.unit}
        onChange={(e) => onChange({ ...step, within: { ...step.within, unit: e.target.value } })}
      >
        {units.map((u: TimeUnit) => <option key={u.name} value={u.label}>{u.label}</option>)}
      </select>

      {/*
        The whole Q3 distinction, as one button. The object does not change; it
        moves between `where` and a rate measure's `of`, and the meaning moves
        with it.
      */}
      <button type="button" className="adm-btn adm-btn--quiet" onClick={onMove}>
        {slot === 'filter'
          ? 'Measure the share instead'
          : `Filter the ${subject}s instead`}
      </button>
      <button type="button" className="adm-btn adm-btn--quiet" onClick={onRemove}>Remove</button>
    </div>
  )
}

// ----------------------------------------------------------------- measures

interface MeasureRowProps {
  cat: SubjectCatalog
  measure: Measure
  steps: KnownStep[]
  onChange: (m: Measure) => void
  onRemove?: () => void
}

/**
 * One measure. What it can be measured OVER comes from the catalog's `operand`:
 * `none` takes nothing, `property` takes a number, `condition` takes a sequence
 * (authored by the sequence row, not here).
 */
export function MeasureRow({ cat, measure, steps, onChange, onRemove }: MeasureRowProps) {
  const fn = cat.measures.find((m) => m.name === measure.fn)

  /** Numbers this measure could average: the subject's, and each step's. */
  const operands: Array<{ key: string; label: string; step?: string; property: string }> = []
  for (const p of cat.properties) {
    if (p.type === 'number') {
      operands.push({ key: p.name, label: p.label, property: p.name })
    }
  }
  for (const s of steps) {
    const ev = eventBy(cat, s.event)
    for (const p of ev?.properties ?? []) {
      if (p.type === 'number') {
        operands.push({
          key: `${s.id}.${p.name}`, label: `${p.label} (${ev?.label ?? s.event})`,
          step: s.id, property: p.name,
        })
      }
    }
  }

  const current = isPropertyOperand(measure.of)
    ? `${measure.of.step ? `${measure.of.step}.` : ''}${measure.of.property}`
    : ''

  const refn = (name: string): void => {
    const next = cat.measures.find((m) => m.name === name)
    const out: Measure = { id: measure.id, fn: name }
    if (measure.label) out.label = measure.label
    // A count takes no operand and a rate's operand is a sequence the analyst
    // authors elsewhere; only property-operand measures keep a carried operand.
    if (next?.operand === 'property' && isPropertyOperand(measure.of)) out.of = measure.of
    onChange(out)
  }

  return (
    <div className="adm-row vb-row">
      <input
        className="adm-input vb-label" type="text" aria-label="Measure label"
        value={measure.label ?? ''} placeholder={fn?.label ?? measure.fn}
        onChange={(e) => onChange({ ...measure, label: e.target.value || undefined })}
      />
      <select
        className="adm-select" aria-label="Measure"
        value={measure.fn} onChange={(e) => refn(e.target.value)}
      >
        {cat.measures
          .filter((m) => m.operand !== 'condition' || isSequence(measure.of))
          .map((m) => <option key={m.name} value={m.name}>{m.label}</option>)}
      </select>

      {fn?.operand === 'property' ? (
        <select
          className="adm-select" aria-label="Measured property"
          value={current}
          onChange={(e) => {
            const chosen = operands.find((o) => o.key === e.target.value)
            if (!chosen) return
            onChange({
              ...measure,
              of: chosen.step
                ? { step: chosen.step, property: chosen.property }
                : { property: chosen.property },
            })
          }}
        >
          <option value="">choose…</option>
          {operands.map((o) => <option key={o.key} value={o.key}>{o.label}</option>)}
        </select>
      ) : null}

      {fn?.operand === 'condition' ? (
        <span className="adm-note">of the sequence below</span>
      ) : null}

      {onRemove ? (
        <button type="button" className="adm-btn adm-btn--quiet" onClick={onRemove}>Remove</button>
      ) : null}
    </div>
  )
}

// --------------------------------------------------------------- breakdowns

/**
 * The half-extents this axis has to tile, across the selected matches.
 *
 * Not one number: a selection can span pitches of different sizes, and a width
 * that stops on the touchline of one straddles the touchline of another. The
 * geometry is the server's measurement — this control holds no pitch
 * dimensions of its own, because the catalog's nominal is right for four of
 * dev's twenty-eight matches and would give a confident wrong answer for the
 * rest. A match with no measured geometry contributes nothing rather than a
 * guess, so an unmeasured selection simply says nothing here.
 */
function halves(
  prop: Property | undefined,
  pitch: Record<string, PitchDims> | undefined,
  games: string[],
): number[] {
  if (!prop?.pitch_dimension) return []
  const out = new Set<number>()
  for (const g of games) {
    const dims = pitch?.[g]
    if (dims) out.add(halfOf(dims, prop.pitch_dimension))
  }
  return [...out].sort((a, b) => a - b)
}

interface BreakdownRowProps {
  cat: SubjectCatalog
  breakdown: Breakdown
  /** Measured geometry per match, as the server reported it. */
  pitch?: Record<string, PitchDims>
  /** The matches currently selected, which is what decides the geometry. */
  games?: string[]
  onChange: (b: Breakdown) => void
  onRemove: () => void
}

/**
 * One breakdown. A number can be binned; the bin's unit is the property's own,
 * so nothing here converts anything.
 */
export function BreakdownRow(
  { cat, breakdown, pitch, games, onChange, onRemove }: BreakdownRowProps,
) {
  const prop = breakdown.property ? propertyBy(cat, breakdown.property) : undefined

  // Every figure below comes from the measured half-extents of the selected
  // matches. No geometry (nothing selected yet, or nothing measured) means no
  // claim: `edges` is empty and the row shows neither a warning nor a datalist.
  const edges = halves(prop, pitch, games ?? [])
  const width = breakdown.bins?.width
  const over = width && edges.length
    ? Math.max(...edges.map((h) => overrun(h, width)))
    : 0
  // Suggested widths only when the selection is one shape: the widths that
  // tile 52.5 straddle 53.5, so a merged list would recommend a broken width.
  const tiling = edges.length === 1 ? tilingWidths(edges[0] as number) : []

  const retarget = (name: string): void => {
    const next = propertyBy(cat, name)
    const out: Breakdown = { property: name }
    if (next?.type === 'number' && breakdown.bins) {
      out.bins = { width: breakdown.bins.width }
      if (next.unit) out.bins.unit = next.unit
    }
    if (breakdown.min_subjects !== undefined) out.min_subjects = breakdown.min_subjects
    onChange(out)
  }

  return (
    <div className="adm-row vb-row">
      <select
        className="adm-select" aria-label="Break down by"
        value={breakdown.property ?? ''} onChange={(e) => retarget(e.target.value)}
      >
        {cat.properties.map((p) => <option key={p.name} value={p.name}>{p.label}</option>)}
      </select>

      {prop?.type === 'number' ? (
        <>
          <span className="adm-note">in bins of</span>
          <input
            className="adm-input vb-num" type="number" min={1} aria-label="Bin width"
            list={tiling.length ? `tiling-${prop.name}` : undefined}
            value={asText(breakdown.bins?.width)}
            onChange={(e) => {
              const width = Number(e.target.value)
              const bins = { width, ...(prop.unit ? { unit: prop.unit } : {}) }
              onChange({ ...breakdown, bins: e.target.value === '' ? undefined : bins })
            }}
          />
          {prop.unit_label ? <span className="adm-note">{prop.unit_label}</span> : null}
          {/*
            Suggested, never enforced. Any width still compiles; one that does
            not tile the axis comes back with a note on the plan saying by how
            much its outer bands overrun.
          */}
          {tiling.length ? (
            <datalist id={`tiling-${prop.name}`}>
              {tiling.map((w) => <option key={w} value={w} />)}
            </datalist>
          ) : null}
          {over > 0 ? (
            <span className="adm-warn">
              overruns the {prop.label.toLowerCase()} edge by {round(over)}
              {prop.unit ?? ''} — the measured half-{prop.pitch_dimension} is{' '}
              {edges.length === 1
                ? `±${round(edges[0] as number)}${prop.unit ?? ''}`
                : `±${round(edges[0] as number)} to ±${round(edges[edges.length - 1] as number)}${prop.unit ?? ''}`}
              {tiling.length
                ? `, which tiles at ${tiling.map(round).join(', ')}${prop.unit ?? ''}`
                : ''}
            </span>
          ) : null}
        </>
      ) : null}

      {/*
        Every breakdown can carry a floor, binned or not. It is what stops a
        100% rate off two subjects being read as a finding, and a binned axis
        needs it exactly as much as a categorical one — Q9 asks for both on the
        same row. The engine applies the highest floor across the breakdowns to
        the whole group, which is why this says "per group" and not "each".
      */}
      <span className="adm-note">at least</span>
      <input
        className="adm-input vb-num" type="number" min={1} aria-label="Minimum subjects"
        value={asText(breakdown.min_subjects)}
        onChange={(e) => onChange({
          ...breakdown,
          min_subjects: e.target.value === '' ? undefined : Number(e.target.value),
        })}
      />
      <span className="adm-note">{cat.subject.plural} per group</span>

      <button type="button" className="adm-btn adm-btn--quiet" onClick={onRemove}>Remove</button>
    </div>
  )
}
